/* eslint-disable react/prop-types */
import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {toCapitalCase} from '@s-ui/js/lib/string'
import AtomButton, {atomButtonDesigns, atomButtonSizes} from '@s-ui/react-atom-button'
import {atomTagDesigns} from '@s-ui/react-atom-tag'
import Context from '@s-ui/react-context'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import TagChip, {tagChipSizes} from '@adv-mt/mt-tag-chip'
import {tagChipTypes} from '@adv-mt/mt-tag-chip/src/index.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {PLACES, SEARCH_USE_CASE_BY_PLACE} from './config.js'
import useSearchTags from './use-search-tags.js'

const FIRST_PAGE = 1

function SearchTags({
  dealerSlug,
  showLabel = true,
  isExtended = true,
  listingSectionId,
  onRemoveTag,
  pageName,
  place,
  search,
  type: tagType,
  design = atomTagDesigns.SOLID,
  size = tagChipSizes.SMALL
}) {
  const {domain, i18n} = useContext(Context)
  const tagsFromSearch = useSearchTags({search, pageName})
  const {track} = useAnalyticsTrack()
  const delayToNextFrame = useDelayToNextFrame()
  const [tags, setTags] = useState(tagsFromSearch || [])

  const config = domain.get('config')
  const {FILTER_NAMES, DEFAULT_SEARCH} = config.get('search')

  const hasTags = Boolean(tags?.length)

  const {
    ADS_LIST_SUBSCRIPTION_PAGE_NAME,
    TECHNICAL_DETAILS_SEARCH_PAGE_NAME,
    TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME
  } = config.get('page')

  useEffect(() => {
    setTags(tagsFromSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const handleRemoveTag = ({field, label, resetValue, extraSearch}) => {
    setTags(prevState => prevState.filter(tag => tag.label !== label))

    delayToNextFrame(() => {
      const isMake = field === FILTER_NAMES.MAKE
      const isModel = field === FILTER_NAMES.MODEL
      const isVersion = field === FILTER_NAMES.VERSION

      const isBodyTypeCategory = field === FILTER_NAMES.BODY_TYPE_CATEGORY

      const fieldName = isBodyTypeCategory ? FILTER_NAMES.BODY_TYPE : field

      const nextSearch = {
        ...search,
        page: FIRST_PAGE,
        [fieldName]: resetValue,
        ...(isMake && {
          make: resetValue,
          model: DEFAULT_SEARCH[FILTER_NAMES.MODEL]
        }),
        // Needed to prevent side effects until multi make model version filter was implemented
        ...((isMake || isModel || isVersion) && {
          vehicles: DEFAULT_SEARCH[FILTER_NAMES.VEHICLES]
        }),
        ...extraSearch
      }

      onRemoveTag({search: nextSearch})

      // TO-DO remove when data collects the required amount of events
      // Related Tech-Debt tiquet to clean code: https://jira.ets.mpi-internal.com/browse/MTR-75307
      setTimeout(
        () =>
          track({
            event: 'Button Clicked',
            data: {label: 'filters tags deleted', hit_information: field}
          }),
        0
      )

      switch (pageName) {
        case TECHNICAL_DETAILS_SEARCH_PAGE_NAME:
        case TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME:
          domain.get('get_search_url_vehicles_use_case').execute({search: nextSearch, pageName})
          break
        default:
          domain
            .get(
              // Progressive rollout for new get_url_params_search_use_case
              SEARCH_USE_CASE_BY_PLACE[place] || 'get_search_url_ads_use_case'
            )
            .execute({dealerSlug, search: nextSearch, listingSectionId, pageName})
          break
      }
    })
  }

  const onResetFilters = () => {
    const {categoryId, offerTypeId, filteredOfferTypeId, targetBuyer} = search
    const nextSearch = {
      categoryId,
      offerTypeId,
      filteredOfferTypeId,
      ...(pageName === ADS_LIST_SUBSCRIPTION_PAGE_NAME ? {targetBuyer} : {})
    }

    switch (pageName) {
      case TECHNICAL_DETAILS_SEARCH_PAGE_NAME:
      case TECHNICAL_DETAILS_SEARCH_RESULTS_PAGE_NAME:
        domain.get('get_search_url_vehicles_use_case').execute({search: {}, pageName})
        break
      default: {
        domain
          .get(
            // Progressive rollout for new get_url_params_search_use_case
            SEARCH_USE_CASE_BY_PLACE[place] || 'get_search_url_ads_use_case'
          )
          .execute({
            dealerSlug,
            search: nextSearch,
            listingSectionId
          })
        break
      }
    }
  }

  return hasTags ? (
    <>
      {showLabel && isExtended && <p className="mt-SearchTags-label">{i18n.t('SEARCH.YOUR_SEARCH')}</p>}
      {tags.map((tag, index) => {
        const defaultType = isExtended ? {type: tagChipTypes.CLEAR} : {}
        const type = tagType ? {type: tagType} : defaultType
        return (
          <div className="mt-SearchTags-tag">
            <TagChip
              {...type}
              closeIcon={<IconClose />}
              closeLabel={`${i18n.t('COMMON.PLURALS.REMOVE_FILTER', 0)} ${tag.label}`}
              design={design || atomTagDesigns.SOLID}
              isFitted={!isExtended}
              key={index}
              label={tag.preserveCase ? tag.label : toCapitalCase(tag.label)}
              onClose={() => handleRemoveTag(tag)}
              responsive={isExtended}
              size={size}
            />
          </div>
        )
      })}
      {isExtended && (
        <div className="mt-SearchTags-reset">
          <AtomButton design={atomButtonDesigns.FLAT} onClick={onResetFilters} size={atomButtonSizes.SMALL}>
            {i18n.t('COMMON.CLEAR_FILTERS')}
          </AtomButton>
        </div>
      )}
    </>
  ) : null
}

SearchTags.displayName = 'SearchTags'
SearchTags.propTypes = {
  showLabel: PropTypes.bool,
  isExtended: PropTypes.bool,
  listingSectionId: PropTypes.string,
  pageName: PropTypes.string,
  place: PropTypes.string,
  search: PropTypes.object,
  tagType: PropTypes.string,
  design: PropTypes.string,
  size: PropTypes.string
}

export {useSearchTags, PLACES as searchTagsPlaces}
export default SearchTags
